import { ArrowUpRight, Brain, UserCheck, FileSpreadsheet, Building2, Calculator, Banknote } from "lucide-react";
import Reveal, { Stagger, StaggerItem } from "../motion/Reveal";
import FlowBackdrop from "../illustrations/FlowBackdrop";

const SOLUTIONS = [
  {
    icon: Brain,
    title: "FCNR Portfolio",
    tag: "Currency-aware",
    body: "Foreign currency portfolios that auto-rebalance around rupee trends, monsoon patterns and RBI policy moves — all explained in real time.",
    points: ["INR/USD tracking", "Monsoon hedging", "AI-flagged alerts"],
    image: "https://images.pexels.com/photos/6801648/pexels-photo-6801648.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
  },
  {
    icon: UserCheck,
    title: "SEBI-registered advisors",
    tag: "1:1 guidance",
    body: "A SEBI-certified advisor mapped to your time zone, host country tax rules, and India financial goals — responds within 12 hours.",
    points: ["US/UK/UAE expertise", "Hindi/English support"],
    image: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
  },
  {
    icon: Building2,
    title: "Indian real estate",
    tag: "FCNR-to-property",
    body: "Commercial real estate in Bangalore, Mumbai, Delhi with FEMA-cleared titles, 8-10% rental yields, and co-investment structures.",
    points: ["8-10% yields", "Tier-1 hubs only"],
    image: "https://images.pexels.com/photos/1732414/pexels-photo-1732414.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
  },
  {
    icon: FileSpreadsheet,
    title: "Compliance & filings",
    tag: "RBI-aligned",
    body: "FEMA filings, Form 67 for rupee deposits, TCS certificates, NRE-to-NRO conversions, and annual DTAA declarations auto-compiled.",
    points: ["Form 67 ready", "TCS tracking", "7-year vault"],
    image: "https://images.pexels.com/photos/6863183/pexels-photo-6863183.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
  },
  {
    icon: Calculator,
    title: "Tax structuring",
    tag: "DTAA + India CA",
    body: "Dual-residency tax planning, DTAA relief maximization, loss harvesting timed to your host country's fiscal calendar, and HUF setup guidance.",
    points: ["DTAA filings", "CA network", "Loss harvesting"],
    image: "https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
  },
  {
    icon: Banknote,
    title: "Repatriation engine",
    tag: "USD 1M pathway",
    body: "Move capital back under RBI's USD 1M liberalized scheme with bank coordination, tax certification, and CA sign-off — all tracked in one place.",
    points: ["USD 1M eligible", "Real-time tracking"],
    image: "https://images.pexels.com/photos/4968391/pexels-photo-4968391.jpeg?auto=compress&cs=tinysrgb&w=600&h=300&fit=crop",
  },
];

export default function Solutions() {
  return (
    <section
      id="solutions"
      data-testid="solutions-section"
      className="py-20 lg:py-28 relative overflow-hidden border-y-2 border-[hsl(var(--zt-gold))]/15"
      style={{ background: "rgba(255,255,255,0.15)" }}
    >
      {/* Indian-inspired multi-layer accents */}
      <div
        aria-hidden
        className="absolute -top-20 right-[-10%] w-[500px] h-[500px] rounded-full opacity-[0.08] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-gold)) 0%, transparent 65%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-[-15%] left-[-5%] w-[450px] h-[450px] rounded-full opacity-[0.07] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-sage)) 0%, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute top-1/3 left-1/2 w-[350px] h-[350px] rounded-full opacity-[0.05] blur-3xl pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(var(--zt-teal)) 0%, transparent 70%)" }}
      />
      
      {/* Indian cultural top border accent */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[hsl(var(--zt-gold))] via-[hsl(var(--zt-sage))] to-transparent opacity-60" />
      
      <FlowBackdrop variant="light" pattern="flow" className="opacity-40" />

      <div className="max-w-[1320px] mx-auto px-6 md:px-12 lg:px-16 relative z-10">
        <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <div className="max-w-2xl space-y-5">
            <div className="flex items-center gap-3">
              <span className="gold-rule" style={{background: "linear-gradient(90deg, hsl(var(--zt-gold)), hsl(var(--zt-sage)))"}} />
              <span className="eyebrow" style={{color: "hsl(var(--zt-gold))"}}>Our Platform</span>
            </div>
            <h2 className="font-display text-[2rem] md:text-[2.6rem] lg:text-[3.1rem] leading-[1.1] text-[hsl(var(--zt-teal-deep))]">
              Six solutions for{" "}
              <span className="text-[hsl(var(--zt-gold))]">overseas Indians</span> seeking India growth.
            </h2>
          </div>
          <p className="text-[15px] text-[hsl(var(--zt-charcoal))]/80 leading-[1.7] max-w-sm font-body">
            From FCNR deposits to rupee repatriation — every tool an NRI needs, wrapped in FEMA compliance and guided by SEBI advisors.
          </p>
        </Reveal>

        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" staggerChildren={0.07}>
          {SOLUTIONS.map((s, i) => {
            const { icon: Icon, title, body, tag, points } = s;
            return (
              <StaggerItem key={title}>
                <article
                  data-testid={`solution-card-${i}`}
                  className="group relative h-full flex flex-col rounded-2xl border-2 border-[hsl(var(--zt-beige-deep))] bg-white overflow-hidden elev-2 transition-all duration-300 hover:-translate-y-1.5 hover:elev-3 hover:border-[hsl(var(--zt-gold))]/60 hover:shadow-xl hover:shadow-[hsl(var(--zt-gold))]/15"
                >
                  {/* Stock image header */}
                  <div className="relative h-40 overflow-hidden bg-[hsl(var(--zt-teal-deep))]">
                    <img
                      src={s.image}
                      alt={title}
                      className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                  </div>

                  <div className="p-6 lg:p-7 flex-1 flex flex-col">
                    <div className="flex items-center justify-between mb-5">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[hsl(var(--zt-gold))]/20 to-[hsl(var(--zt-gold))]/5 grid place-items-center transition-all duration-300 group-hover:from-[hsl(var(--zt-gold))]/30 group-hover:to-[hsl(var(--zt-gold))]/15 group-hover:shadow-lg group-hover:shadow-[hsl(var(--zt-gold))]/20">
                        <Icon size={20} className="text-[hsl(var(--zt-gold))] group-hover:scale-110 transition-transform" />
                      </div>
                      <span className="font-heading text-[10px] uppercase tracking-[0.15em] px-3 py-1.5 rounded-lg font-bold text-white bg-gradient-to-r from-[hsl(var(--zt-gold))] to-[hsl(var(--zt-gold))]/80 border border-[hsl(var(--zt-gold))]/50 shadow-sm">
                        {tag}
                      </span>
                    </div>

                    <h3 className="font-heading text-[1.15rem] font-semibold mb-2.5 tracking-tight text-[hsl(var(--zt-teal-deep))]">
                      {title}
                    </h3>
                    <p className="text-[13.5px] leading-relaxed font-body mb-5 text-[hsl(var(--zt-charcoal))]/75">
                      {body}
                    </p>

                    {/* Feature checklist */}
                    <ul className="space-y-2.5 mb-6">
                      {points.map((p) => (
                        <li key={p} className="flex items-start gap-2.5">
                          <span className="h-2 w-2 rounded-full flex-shrink-0 bg-[hsl(var(--zt-gold))] mt-1" />
                          <span className="font-heading text-[12.5px] font-medium text-[hsl(var(--zt-charcoal))]/80">
                            {p}
                          </span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-center gap-1.5 mt-auto font-heading text-[12px] font-semibold text-[hsl(var(--zt-gold))] group-hover:text-[hsl(var(--zt-sage))] transition-colors duration-300">
                      Learn more
                      <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </div>
                  </div>
                </article>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </section>
  );
}
